<template>
    <div class="space-y-6">
        <div class="flex items-center justify-between">
            <h2 class="text-heading-md">Phone Input Examples</h2>
            <div class="flex items-center gap-4">
                <span class="text-content-md font-medium text-content-secondary">Size:</span>
                <DsRadio
                    v-model="size"
                    label="Medium (default)"
                    name="phone-input-size"
                    value="md"
                />
                <DsRadio
                    v-model="size"
                    label="Small"
                    name="phone-input-size"
                    value="sm"
                />
            </div>
        </div>

        <!-- Phone Input -->
        <h3 class="mb-4 border-b border-ds-primary-200 pb-2 text-heading-sm">Phone Input</h3>

        <div class="grid grid-cols-2 items-start gap-x-16 gap-y-6">
            <div class="space-y-2">
                <DsPhoneInput
                    v-model="phoneNumber"
                    :defaultCountry="countryCode"
                    label="Phone number"
                    labelTooltip="Info icon tooltip"
                    :size
                    @countryChanged="countryCode = $event"
                />
                <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                    <p><strong>Country:</strong> {{ countryCode.name }}</p>
                    <p><strong>Dial code:</strong> +{{ countryCode.dialCode }}</p>
                    <p><strong>ISO2:</strong> {{ countryCode.iso2 }}</p>
                    <p><strong>Phone number:</strong> {{ phoneNumber }}</p>
                </div>
            </div>

            <div class="space-y-2">
                <DsPhoneInput
                    v-model="phoneNumberError"
                    :defaultCountry="countryCodeError"
                    error="Enter a valid phone number"
                    label="Phone number"
                    :size
                    @countryChanged="countryCodeError = $event"
                />
                <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                    <p><strong>Country:</strong> {{ countryCodeError.name }}</p>
                    <p><strong>Dial code:</strong> +{{ countryCodeError.dialCode }}</p>
                    <p><strong>ISO2:</strong> {{ countryCodeError.iso2 }}</p>
                    <p><strong>Phone number:</strong> {{ phoneNumberError }}</p>
                </div>
            </div>

            <div class="space-y-2">
                <DsPhoneInput
                    v-model="phoneNumberWarning"
                    :defaultCountry="countryCodeWarning"
                    label="Phone number"
                    :size
                    warning="Warning message"
                    @countryChanged="countryCodeWarning = $event"
                />
                <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                    <p><strong>Country:</strong> {{ countryCodeWarning.name }}</p>
                    <p><strong>Dial code:</strong> +{{ countryCodeWarning.dialCode }}</p>
                    <p><strong>ISO2:</strong> {{ countryCodeWarning.iso2 }}</p>
                    <p><strong>Phone number:</strong> {{ phoneNumberWarning }}</p>
                </div>
            </div>

            <div class="space-y-2">
                <DsPhoneInput
                    v-model="phoneNumberHelperText"
                    :defaultCountry="countryCodeHelperText"
                    helperText="This is a helper text"
                    label="Phone number"
                    :size
                    @countryChanged="countryCodeHelperText = $event"
                />
                <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                    <p><strong>Country:</strong> {{ countryCodeHelperText.name }}</p>
                    <p><strong>Dial code:</strong> +{{ countryCodeHelperText.dialCode }}</p>
                    <p><strong>ISO2:</strong> {{ countryCodeHelperText.iso2 }}</p>
                    <p><strong>Phone number:</strong> {{ phoneNumberHelperText }}</p>
                </div>
            </div>

            <DsPhoneInput
                v-model="phoneNumberDisabled"
                disabled
                label="Phone number"
                :size
            />

            <DsPhoneInput
                v-model="phoneNumberRequired"
                label="Phone number"
                required
                :size
            />

            <DsPhoneInput
                v-model="phoneNumberCustomTooltip"
                label="Phone number"
                :size
            >
                <template #labelTooltip>
                    <div>
                        Custom tooltip with
                        <strong>rich content</strong>
                    </div>
                </template>
            </DsPhoneInput>

            <div class="space-y-2">
                <DsPhoneInput
                    v-model="phoneNumberValidity"
                    label="Phone number"
                    :size
                    @validityChanged="isPhoneValid = $event"
                />
                <div class="rounded-lg bg-ds-primary-50 p-3 text-sm">
                    <p><strong>Valid:</strong> {{ isPhoneValid }}</p>
                </div>
            </div>
        </div>

        <!-- Prefilled Phone Numbers -->
        <h3 class="mb-4 border-b border-ds-primary-200 pb-2 text-heading-sm">Prefilled Phone Numbers</h3>

        <div class="grid grid-cols-3 gap-4">
            <div class="space-y-2">
                <DsPhoneInput
                    v-model="usPhoneNumber"
                    :defaultCountry="usCountry"
                    label="US Phone Number"
                    :preferredCountries="preferredCountries"
                    :size
                    @countryChanged="usCountry = $event"
                />
                <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                    <p><strong>Input (E164):</strong> {{ usPhoneNumber }}</p>
                    <p><strong>Country:</strong> {{ usCountry.name }}</p>
                    <p><strong>ISO2:</strong> {{ usCountry.iso2 }}</p>
                </div>
            </div>

            <div class="space-y-2">
                <DsPhoneInput
                    v-model="ukPhoneNumber"
                    :defaultCountry="ukCountry"
                    label="UK Phone Number"
                    :preferredCountries="preferredCountries"
                    :size
                    @countryChanged="ukCountry = $event"
                />
                <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                    <p><strong>Input (E164):</strong> {{ ukPhoneNumber }}</p>
                    <p><strong>Country:</strong> {{ ukCountry.name }}</p>
                    <p><strong>ISO2:</strong> {{ ukCountry.iso2 }}</p>
                </div>
            </div>

            <div class="space-y-2">
                <DsPhoneInput
                    v-model="canadaPhoneNumber"
                    :defaultCountry="canadaCountry"
                    label="Canada Phone Number"
                    :preferredCountries="preferredCountries"
                    :size
                    @countryChanged="canadaCountry = $event"
                />
                <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                    <p><strong>Input (E164):</strong> {{ canadaPhoneNumber }}</p>
                    <p><strong>Country:</strong> {{ canadaCountry.name }}</p>
                    <p><strong>ISO2:</strong> {{ canadaCountry.iso2 }}</p>
                </div>
            </div>
        </div>

        <!-- In a Sheet -->
        <h3 class="mb-4 border-b border-ds-primary-200 pb-2 text-heading-sm">In a Sheet</h3>

        <DsButton
            size="md"
            variant="primary"
            @click="isSheetOpen = true"
        >
            Open Sheet with Phone Input
        </DsButton>

        <Teleport to="body">
            <DsSheet
                v-model:open="isSheetOpen"
                primaryLabel="Submit"
                secondaryLabel="Cancel"
                title="Phone Input Examples"
                @clickPrimary="validatePhoneNumber"
                @clickSecondary="isSheetOpen = false"
            >
                <div class="space-y-4">
                    <DsPhoneInput
                        v-model="phoneNumberInSheet"
                        :error="hasPhoneNumberError"
                        :helperText
                        label="Phone number"
                        required
                        :size
                        @countryChanged="handleCountryChangedInSheet"
                    />
                    <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                        <p><strong>Phone number:</strong> {{ phoneNumberInSheet }}</p>
                        <p><strong>Country:</strong> {{ countryCodeInSheet?.name }}</p>
                        <p><strong>Dial code:</strong> +{{ countryCodeInSheet?.dialCode }}</p>
                        <p><strong>ISO2:</strong> {{ countryCodeInSheet?.iso2 }}</p>
                    </div>
                </div>
            </DsSheet>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import DsSheet from '@/components/design-system/DsSheet.vue';
import DsButton from '@/components/design-system/button/DsButton.vue';
import DsPhoneInput from '@/components/design-system/form/input/DsPhoneInput/DsPhoneInput.vue';
import type { Country } from '@/components/design-system/form/input/DsPhoneInput/phone-input.types';
import { getCountryFromIso2, isValid } from '@/shared/utils/phone-number';
import DsRadio from '@ds/form/input/DsRadio.vue';
import type { DsInputSize } from '@ds/form/input/text/text-input.types';
import type { CountryCode } from 'libphonenumber-js';
import { ref } from 'vue';

const size = ref<DsInputSize>('md');

const preferredCountries: CountryCode[] = ['US', 'CA', 'GB'];

const defaultCountry = getCountryFromIso2('US');

const phoneNumber = ref('');
const countryCode = ref<Country>(defaultCountry);

const phoneNumberError = ref('');
const countryCodeError = ref<Country>(defaultCountry);

const phoneNumberWarning = ref('');
const countryCodeWarning = ref<Country>(defaultCountry);

const phoneNumberHelperText = ref('');
const countryCodeHelperText = ref<Country>(defaultCountry);

const phoneNumberRequired = ref('');
const phoneNumberCustomTooltip = ref('');
const phoneNumberValidity = ref('');
const isPhoneValid = ref(false);

const phoneNumberDisabled = ref('');

// Three prefilled examples from different countries
const usPhoneNumber = ref('+14155552671');
const usCountry = ref<Country>(getCountryFromIso2('US'));

const ukPhoneNumber = ref('+442079460958');
const ukCountry = ref<Country>(getCountryFromIso2('GB'));

const canadaPhoneNumber = ref('+12045550123');
const canadaCountry = ref<Country>(getCountryFromIso2('CA'));

const hasPhoneNumberError = ref('');
const phoneNumberInSheet = ref('+14155552671');
const countryCodeInSheet = ref<Country>();

const helperText = ref('');
const isSheetOpen = ref(false);

const validatePhoneNumber = () => {
    if (!isValid(phoneNumberInSheet.value)) {
        hasPhoneNumberError.value = 'Invalid phone number';
        helperText.value = '';
    } else {
        helperText.value = 'Phone number is valid';
        hasPhoneNumberError.value = '';
    }
};

const handleCountryChangedInSheet = (country: Country) => {
    countryCodeInSheet.value = country;
    validatePhoneNumber();
};
</script>
